import { Check as CheckIcon, TriangleAlert, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { centsEur, checkSummary, checkSummaryLine } from "@/lib/documents";
import type { DocumentDetail, Finding, OpenShare } from "@/lib/types";
import { cn } from "@/lib/utils";
import type { DocumentWrites } from "./useDocument";

const OMIT_REASON = "absichtlich weglassen";
const shortDate = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

function StatusIcon({ status }: { status: "ok" | "warning" | "error" }) {
  const cls = "mt-px size-4 shrink-0";
  if (status === "ok") return <CheckIcon aria-hidden strokeWidth={2.4} className={cn(cls, "text-chart-1")} />;
  if (status === "warning") return <TriangleAlert aria-hidden className={cn(cls, "text-warning")} />;
  return <X aria-hidden strokeWidth={2.4} className={cn(cls, "text-destructive")} />;
}

const STATUS_WORD = { ok: "bestanden", warning: "Warnung", error: "nicht bestanden" } as const;

function Line({ status, label, children }: { status: "ok" | "warning" | "error"; label: string; children?: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-[0.8125rem] leading-[1.4]">
      <StatusIcon status={status} />
      <div className="flex min-w-0 flex-col gap-1">
        <span>
          {label}
          <span className="sr-only"> – {STATUS_WORD[status]}</span>
        </span>
        {children}
      </div>
    </li>
  );
}

function Messages({ findings, tone }: { findings: Finding[]; tone: "error" | "warning" }) {
  return findings.map((f) => (
    <span key={f.code + f.message} className={cn("text-xs", tone === "error" ? "text-destructive" : "text-muted-foreground")}>{f.message}</span>
  ));
}

/** Rule 7: each forgotten share can be taken onto the draft or left off on purpose. */
function MissingShares({ shares, writes }: { shares: OpenShare[]; writes: DocumentWrites }) {
  const [busy, setBusy] = useState<number | null>(null);
  async function act(id: number, call: () => Promise<unknown>) {
    setBusy(id);
    try {
      await call();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  }
  return (
    <ul className="flex flex-col gap-1">
      {shares.map((s) => (
        <li key={s.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          <span className="min-w-0 truncate text-muted-foreground">{s.payee} · {shortDate(s.valueDate)} · {centsEur(s.rebillCents)}</span>
          <span className="flex gap-1">
            <Button variant="outline" size="xs" className="pointer-coarse:h-11" disabled={busy !== null} onClick={() => act(s.id, () => writes.addItems({ expenseShareIds: [s.id] }))}>
              mitnehmen
            </Button>
            <Button variant="ghost" size="xs" className="pointer-coarse:h-11" disabled={busy !== null} onClick={() => act(s.id, () => writes.omitShare(s.id, OMIT_REASON))}>
              absichtlich weglassen
            </Button>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** „<n> von <m> Prüfungen bestanden“: the eight rules, plus every blocking error outside them. */
export function CheckList({ detail, writes }: { detail: DocumentDetail; writes: DocumentWrites }) {
  const v = detail.validation;
  const summary = checkSummary(v);
  const [rebuilding, setRebuilding] = useState(false);
  const otherWarnings = v.warnings.filter((w) => w.rule === null);

  async function rebuild() {
    setRebuilding(true);
    try {
      await writes.rebuildDeductions();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRebuilding(false);
    }
  }

  return (
    <section aria-labelledby="h-checks" className="flex flex-col gap-2.5 rounded-xl border bg-card px-4 py-3.5">
      <h2 id="h-checks" className="text-[0.8125rem] font-semibold">{checkSummaryLine(summary)}</h2>
      <ul className="flex flex-col gap-2.5">
        {v.checks.map((c) => {
          const errors = v.errors.filter((f) => f.rule === c.rule);
          const warnings = v.warnings.filter((f) => f.rule === c.rule);
          const missing = c.rule === 7 && c.status !== "ok" && detail.missingShares.length > 0;
          return (
            <Line key={c.rule} status={c.status} label={c.label}>
              <Messages findings={errors} tone="error" />
              <Messages findings={warnings} tone="warning" />
              {missing && <MissingShares shares={detail.missingShares} writes={writes} />}
            </Line>
          );
        })}
        {summary.extra.map((f) => (
          <Line key={f.code} status="error" label={f.message}>
            {f.code === "offer_deductions_stale" && (
              <Button variant="outline" size="xs" className="self-start pointer-coarse:h-11" disabled={rebuilding} onClick={rebuild}>
                Abzüge neu berechnen
              </Button>
            )}
          </Line>
        ))}
        {otherWarnings.map((f) => <Line key={f.code + f.message} status="warning" label={f.message} />)}
      </ul>
    </section>
  );
}
